import React, {useState} from "react";
import {X, Loader, Tag, Calendar, Package, History} from "lucide-react";
import {useQuery} from '@tanstack/react-query';
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import IOSSwitch from "../../Common/IOSSwitch";
import DateInput from "../../Common/DateInput";

// Manages the conditions under which this discount automatically applies —
// a validity period (start/end date), the client types it targets, the
// product scope (stockable and/or vendable) it applies to, and the client's
// last visit (only clients who came less than N days ago).
// Separated from the main discount form because rules only make sense once
// the discount already exists (they target an existing record).
const DiscountRulesModal = ({
                                 discountId,
                                 initialClientTypeIds = [],
                                 initialStartDate = '',
                                 initialEndDate = '',
                                 initialIsStockable = true,
                                 initialIsVendable = true,
                                 initialMaxDaysSinceLastVisit = '',
                                 onClose,
                                 showNotification,
                                 onSaved
                             }) => {
    const [selectedIds, setSelectedIds] = useState(initialClientTypeIds);
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [isStockable, setIsStockable] = useState(initialIsStockable);
    const [isVendable, setIsVendable] = useState(initialIsVendable);
    const [maxDaysSinceLastVisit, setMaxDaysSinceLastVisit] = useState(initialMaxDaysSinceLastVisit ?? '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const {data: clientTypes = [], isLoading} = useQuery({
        queryKey: ['client-types', 'all'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/client-types?itemsPerPage=100', {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            return response.data.items || [];
        }
    });

    const toggleType = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSave = async () => {
        if (startDate && endDate && endDate < startDate) {
            setError("La date de fin doit être postérieure à la date de début");
            return;
        }
        if (!isStockable && !isVendable) {
            setError("Sélectionnez au moins une option d'application (stockable et/ou vendable)");
            return;
        }
        if (maxDaysSinceLastVisit !== '' && !(Number(maxDaysSinceLastVisit) >= 1)) {
            setError("Le nombre de jours depuis la dernière visite doit être au moins 1");
            return;
        }
        setError('');
        setSubmitting(true);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const payload = {
                clientTypeIds: selectedIds,
                startDate: startDate || null,
                endDate: endDate || null,
                isStockable,
                isVendable,
                maxDaysSinceLastVisit: maxDaysSinceLastVisit === '' ? null : Math.floor(Number(maxDaysSinceLastVisit))
            };
            const response = await api.put(`/discounts/${discountId}/rules`, payload, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            showNotification('success', 'Règles de la remise mises à jour avec succès');
            onSaved?.({
                clientTypeIds: response.data?.clientTypeIds || selectedIds,
                startDate: response.data?.startDate ?? payload.startDate,
                endDate: response.data?.endDate ?? payload.endDate,
                isStockable: response.data?.isStockable ?? payload.isStockable,
                isVendable: response.data?.isVendable ?? payload.isVendable,
                maxDaysSinceLastVisit: response.data
                    ? response.data.maxDaysSinceLastVisit ?? null
                    : payload.maxDaysSinceLastVisit
            });
            onClose();
        } catch (err) {
            const errorMessage = err.response?.data?.detail ||
                err.response?.data?.message ||
                "Une erreur s'est produite";
            showNotification('error', `Erreur: ${errorMessage}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 w-full max-w-lg mx-4 my-8 selection-bar-appear">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                        Règles de la remise
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X className="w-5 h-5"/>
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Validity period */}
                    <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                            <Calendar className="w-4 h-4"/> Période de validité
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Laissez vide pour une remise sans limite de dates.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date début</label>
                                <DateInput
                                    value={startDate || ''}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date fin</label>
                                <DateInput
                                    value={endDate || ''}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate || undefined}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Product scope */}
                    <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                            <Package className="w-4 h-4"/> Application aux produits
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Choisissez si la remise s'applique aux produits stockables, vendables, ou aux deux.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Produits stockables</span>
                                <IOSSwitch checked={isStockable} onChange={() => setIsStockable((prev) => !prev)}/>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Produits vendables</span>
                                <IOSSwitch checked={isVendable} onChange={() => setIsVendable((prev) => !prev)}/>
                            </div>
                        </div>
                    </div>

                    {/* Client last visit */}
                    <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                            <History className="w-4 h-4"/> Dernière visite du client
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Laissez vide pour ne pas tenir compte de la dernière visite. Sinon, la remise ne s'applique
                            qu'à une vente avec un client déjà venu il y a moins de N jours (un client sans visite
                            précédente n'en bénéficie pas).
                        </p>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <span>Dernière visite il y a moins de</span>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                placeholder="—"
                                value={maxDaysSinceLastVisit}
                                onChange={(e) => setMaxDaysSinceLastVisit(e.target.value)}
                                className="w-24 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                            />
                            <span>jours</span>
                        </div>
                    </div>

                    {/* Client types */}
                    <div>
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                            <Tag className="w-4 h-4"/> Types de clients concernés
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Si aucun type n'est sélectionné, la remise ne cible aucun type de client en particulier.
                        </p>

                        {isLoading ? (
                            <div className="flex justify-center items-center h-24">
                                <Loader className="w-6 h-6 text-primary animate-spin"/>
                            </div>
                        ) : clientTypes.length > 0 ? (
                            <div className="space-y-2 max-h-56 overflow-y-auto">
                                {clientTypes.map((clientType) => (
                                    <label
                                        key={clientType.id}
                                        className="flex items-center gap-3 px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(clientType.id)}
                                            onChange={() => toggleType(clientType.id)}
                                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        <Tag className="w-4 h-4 text-gray-400"/>
                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{clientType.name}</span>
                                    </label>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                Aucun type de client configuré. Ajoutez-en un dans Paramètres &gt; Types de clients.
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="px-6 pb-2">
                        <p className="text-xs text-red-500">{error}</p>
                    </div>
                )}

                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                        disabled={submitting}
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        disabled={submitting}
                    >
                        {submitting ? (
                            <div className="flex items-center">
                                <Loader className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"/>
                                Enregistrement...
                            </div>
                        ) : (
                            <span>Enregistrer</span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DiscountRulesModal;
